import { useField } from 'formik';
import merge from 'lodash/merge';
import { useContext } from 'react';

import styles from './FormControl.module.less';
import FormItem from './FormItem';
import { config } from './InputComponentMap';
import { useFormikHelpers } from './useFormikHelpers';
import { useOtherProps } from './useOtherProps';
import { HVFormContext } from '../FormikForm/FormikForm';

import { normalizeCSSID } from '$Validation/Normalizer';

/**********************************
 * FORM CONTROL
 *
 * FormControl is a wrapper for Form Field components
 * (typically from Ant but some from other sources)
 * when using them with Formik.
 *
 * The goal is to provide a consistent API across all types of
 * fields and reduce the overhead having to remember
 * the disparate props / structures various components,
 * as well as reducing the overall number of imports
 * needed for each form.
 *
 * FormControl can and should be used in Static forms,
 * but additionally it is being being developed
 * with DynamicForms v2 in mind, and will
 * be part of that feature.
 *
 **********************************/

// ===== Types it would be good to have:
// - Multiselect in dropdown format, with typeahead
// - Time picker
// - Matrix questions?
// - What else?

const FormControl = props => {
    const {
        children,
        help_text: helpText,
        hideExplainField = false,
        label,
        labelAlignment,
        manualError,
        name,
        onBlur: onBlurRaw,
        onChange: onChangeRaw,
        onFocus: onFocusRaw,
        requiredMark,
        selectOnFocus = false,
        type,
        tooltip,
        maxWidth,
        wrapperStyle,
        wrapperClassName,
        ...otherProps
    } = props;

    // TO DO: verify this plays nice with dynamic forms
    let { id } = props;
    if (!id) id = normalizeCSSID(name);

    // conglomerate of other props passed to FormControl that we selectively allow,
    // including some additional type-specific props
    const fieldPropsRaw = useOtherProps(otherProps, type);

    // Get the default configuration
    let typeConfig = config[type];

    // If none exists, warn
    if (!typeConfig) {
        console.warn(
            `${type} type does not have a configuration / component. Defaulting to text input.`
        );
        typeConfig = config['text'];
    }
    // Get props from FormikForm, even if the control is deeply nested
    const { labelCol, layout, debug } = useContext(HVFormContext);

    const Component = typeConfig.component;
    // Get all the defaults
    // And merge them with specific instance options
    const fieldProps = merge({}, typeConfig.fieldProps || {}, fieldPropsRaw);

    // A NOTE on ADDING CUSTOM EVENT HANDLERS:
    // As can be seen below, custom handlers receive as an argument
    // an object with many resources, including Formik's
    // original onChange handler, as well as the field
    // helper functions (https://formik.org/docs/api/useField#fieldhelperprops)

    // It is up to YOU as developer to decide when and if to call
    // the original handler in your custom function.

    // ***** If you do not set the Formik Field value manually or call the
    // original onChange handler, Formik will NOT receive updated values! *****

    // Ideally we should be limiting the use of custom onChange methods
    // in one-off situations, and testing them well when they are
    // defaults for FieldTypes
    const onChange = typeConfig.onChange || onChangeRaw;
    const onBlur = typeConfig.onBlur || onBlurRaw;
    const onFocus = typeConfig.onFocus || onFocusRaw;

    // Helpers includes `setValue` -- see Formik docs for details
    const [field, meta, helpers] = useField(name);
    const { error, touched } = meta;

    // make Formik helpers available if need to call them directly.
    // also makes additional debug logging available when `debug={true}` on FormikForm component.
    const { callback, formikOnChange, formikOnBlur, formikHelpers } =
        useFormikHelpers({
            debug,
            field,
            helpers,
            name
        });

    const errorID = `${id}-error`;
    const helpTextID = `${id}-help-text`;
    const describerID = error ? errorID : helpText ? helpTextID : undefined;

    const finalFieldProps = {
        ...field,
        ...fieldProps,
        'aria-describedby': describerID,
        'aria-invalid': error ? true : undefined,
        id,
        onBlur: onBlur
            ? event => {
                  onBlur({
                      event,
                      formikOnBlur,
                      helpers: formikHelpers,
                      meta
                  });
              }
            : formikOnBlur,
        onChange: onChange
            ? event =>
                  onChange({
                      callback,
                      event,
                      formikOnChange,
                      helpers: formikHelpers,
                      meta
                  })
            : formikOnChange,
        onFocus:
            onFocus || selectOnFocus
                ? event => {
                      if (
                          selectOnFocus &&
                          event.target &&
                          typeof event.target.select === 'function'
                      ) {
                          event.target.select();
                      }
                      if (onFocus) {
                          onFocus({
                              event,
                              helpers: formikHelpers,
                              meta
                          });
                      }
                  }
                : undefined
    };

    // Hidden field is hidden; no wrapper needed
    if (type === 'hidden') return <Component {...finalFieldProps} />;

    // Checkbox to be formatted differently
    // to get the label to align correctly
    const body =
        type === 'checkbox' ? (
            <Component helpers={helpers} meta={meta} {...finalFieldProps}>
                {label}
            </Component>
        ) : (
            <Component helpers={helpers} meta={meta} {...finalFieldProps} />
        );

    // Make sure we don't show error formatting on fields the user hasn't touched yet
    // Pass this down to FormItem, too

    const shouldShowError = !!touched && (manualError || error);
    const finalErrorText = manualError || error;

    // Finally, set up the FormItem wrapper that gives us
    // nicely formatted errors and help messages
    return (
        <FormItem
            shouldShowError={shouldShowError}
            helpText={helpText}
            id={id}
            label={type === 'checkbox' ? undefined : label}
            labelAlignment={labelAlignment}
            labelCol={labelCol}
            layout={layout}
            name={name}
            type={type}
            requiredMark={requiredMark}
            tooltip={tooltip}
            maxWidth={maxWidth}
            style={wrapperStyle}
            className={wrapperClassName}
        >
            {body}
            {children}
            {helpText && (
                <div className={styles['help-text']} id={helpTextID}>
                    {helpText}
                </div>
            )}
            {!hideExplainField && (
                <div
                    className={[
                        styles['ant-form-item-explain'],
                        shouldShowError
                            ? styles['ant-form-item-explain-error']
                            : ''
                    ].join(' ')}
                >
                    <div id={errorID} role="alert">
                        {finalErrorText}
                    </div>
                </div>
            )}
        </FormItem>
    );
};

export default FormControl;
